import { useQuery } from "@tanstack/react-query";
import { dealApi, dealQueryKeys } from "service/crm/api/deal";

interface UseQueryOptions {
  enabled?: boolean;
}

// Step 1 alone: dealKey -> uuid
export const useResolveDealUuid = (
  dealKey: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: dealQueryKeys.resolve(dealKey),
    queryFn: () => dealApi.resolveDealUuidByKey(dealKey),
    enabled: (options.enabled ?? true) && !!dealKey,
  });
};

// Step 2 alone: uuid -> full deal detail
export const useDealDetails = (
  dealUuid: string,
  options: UseQueryOptions = {},
) => {
  const {
    data: deal,
    isLoading,
    isError,
  } = useQuery({
    queryKey: dealQueryKeys.detail(dealUuid),
    queryFn: () => dealApi.getDeal(dealUuid),
    enabled: (options.enabled ?? true) && !!dealUuid,
  });

  return { deal, isLoading, isError };
};

// Composed: dealKey -> uuid -> full deal detail
// This is what the detail page should use — the URL carries the key.
export const useDealDetailsByKey = (dealKey: string) => {
  const {
    data: dealUuid,
    isLoading: isResolving,
    isError: isResolveError,
  } = useResolveDealUuid(dealKey);

  const {
    deal,
    isLoading: isLoadingDetail,
    isError: isDetailError,
  } = useDealDetails(dealUuid ?? "", { enabled: !!dealUuid });

  return {
    dealUuid,
    deal,
    isLoading: isResolving || isLoadingDetail,
    isError: isResolveError || isDetailError,
  };
};
